import * as React from 'react'

import { clsx } from 'clsx'
import { useMutation } from '@apollo/client/react'
import capitalize from 'lodash/capitalize'
import { useLocation, useNavigate } from 'react-router-dom'

import { GetPlanSelectorData } from '@buffer-mono/app-shell'
import { AssistedBillingNotice } from '@buffer-mono/app-shell/src/components/AssistedBillingNotice'
import { OBPlanIdValues } from '@buffer-mono/app-shell/src/components/PlanSelector/types'
import {
  Flex,
  Text,
  Link,
  Button,
  DropdownMenu,
  IconButton,
  EllipsisVerticalIcon,
  Badge,
  Heading,
  toast,
  AlertDialog,
  Notice,
  Tooltip,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import { MODALS, openGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import type { FragmentOf, ResultOf } from '~publish/graphql'
import {
  settingsBillingKeepMyPlanRoute,
  settingsBillingRoute,
} from '~publish/legacy/routes'

import PlanCardsVertical from './PlanCardsVertical'
import type { PlanCardsVertical_WebBillingDetails } from './PlanCardsVertical'

import styles from './PlanContent.module.css'
import inlineTextControls from '../../shared/inlineTextControls.module.css'

import type { JSX } from 'react'

const openBeaconChat = (): void => {
  const helpCenterUrl =
    'https://support.buffer.com/?openContact=true&utm_source=buffer&utm_medium=billing-support-link&utm_campaign=billing-support'
  if (window.Beacon) {
    window.Beacon('navigate', '/ask/message/')
    window.Beacon('open')
  } else {
    window.open(helpCenterUrl, '_blank')
  }
}

const REMOVE_SUBSCRIPTION_SCHEDULE = graphql(/* GraphQL */ `
  mutation removeSubscriptionSchedule($organizationId: OrganizationId!) {
    removeSubscriptionSchedule(organizationId: $organizationId) {
      ... on RemoveSubscriptionScheduleResponse {
        success
      }
      ... on RemoveSubscriptionScheduleError {
        message
      }
    }
  }
`)

const KEEP_SUBSCRIPTION_ACTIVE = graphql(/* GraphQL */ `
  mutation keepSubscriptionActive($input: KeepSubscriptionActiveInput!) {
    keepSubscriptionActive(input: $input) {
      ... on KeepSubscriptionActiveSuccess {
        success
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

/**
 * Fragment for organization data used on the billing page.
 */
export const OrganizationDataForBilling_Organization = graphql(/* GraphQL */ `
  fragment OrganizationDataForBilling_Organization on Organization {
    id
    members {
      totalCount
    }
    channels {
      id
      isLocked
    }
    limits {
      channels
    }
    commonTrackingProperties {
      atEventBillingState
      atEventBillingPlan
      atEventBillingCycle
      atEventBillingGateway
      atEventIsNewBuffer
      atEventChannelQty
    }
  }
`)

export const PlanContent_WebBillingDetails = graphql(/* GraphQL */ `
  fragment PlanContent_WebBillingDetails on WebBillingDetails {
    requiresAssistedBilling
    balance {
      amount
    }
    paymentMethodDetails {
      __typename
    }
    subscription {
      periodEnd
      isCanceledAtPeriodEnd
      canceledAt
      trial {
        startDate
        endDate
      }
      plan {
        id
        name
      }
      scheduledUpdate {
        newPlan
        newInterval
        newQuantity
        effectiveDate
      }
    }
  }
`)

const MS_PER_DAY = 24 * 60 * 60 * 1000

// TODO: check if used elsewhere
/**
 * Formats number to currency with 2 decimals and no decimal for whole numbers
 * @param {number} value number to format.
 * @returns {string} formatted string
 */
export function formatCurrency(value: number): string {
  if (value) {
    if (Number.isInteger(value)) {
      return value.toString()
    } else {
      return value.toFixed(2)
    }
  } else {
    return '0'
  }
}

// Derived from the PlanCardsVertical fragment so the helper and the query stay
// in sync. The fragment aliases `value` → `amountValue`/`percentValue` because
// AmountDiscount.value (Int!) and PercentDiscount.value (Float!) collide under
// GraphQL's field-merging rules.
type DiscountInput = ResultOf<
  typeof PlanCardsVertical_WebBillingDetails
>['discounts'][number]

export const getDiscountCopy = (
  discounts: readonly DiscountInput[],
): {
  discountBadgeCopy: string | null
  discountDescription: string | null
} => {
  // Discounts are ordered by priority; show the first one. The billing page
  // has historically surfaced a single discount badge — the full list is
  // available in the upcoming-invoice breakdown elsewhere.
  const discount = discounts[0]
  if (!discount) {
    return {
      discountBadgeCopy: null,
      discountDescription: null,
    }
  }

  let discountDescription: string
  let discountBadgeCopy: string
  if (discount.__typename === 'AmountDiscount') {
    // AmountDiscount.value is in cents; convert to dollars for display.
    const dollars = discount.amountValue / 100
    discountDescription = `$${formatCurrency(dollars)}`
    discountBadgeCopy = `$${formatCurrency(dollars)} off`
  } else {
    discountDescription = `${discount.percentValue}% off`
    discountBadgeCopy = `${discount.percentValue}% off`
  }

  switch (discount.duration) {
    case 'forever':
      discountDescription += ' forever'
      break
    case 'once':
      discountDescription += ' on your next invoice'
      break
    case 'repeating':
      discountDescription += ` for ${discount.durationInMonths} months`
      break
  }
  return {
    discountBadgeCopy,
    discountDescription,
  }
}

/**
 * Formats the customer's credit balance copy if needed
 * @param creditBalance - The credit balance.
 * @returns The formatted credit balance copy if the customer does not have a balance of 0 in which case it returns null
 */
const getCreditBalanceCopy = (creditBalance: number): JSX.Element | null => {
  if (creditBalance === 0) return null

  // Positive balance: implies that the customer has a debit which will be applied to their next invoice
  if (creditBalance > 0) {
    return (
      <>
        <strong>${formatCurrency(creditBalance)} will be added </strong>
        to your upcoming invoice.
      </>
    )
  }

  // Negative balance: implies that the customer has a credit which will be applied to their next invoice
  return (
    <>
      You have a credit of{' '}
      <strong>${formatCurrency(Math.abs(creditBalance))}</strong> which will be
      automatically applied to your upcoming invoices.
    </>
  )
}

/**
 * Formats a number to a currency string.
 * @param amount - The amount to format.
 * @param currency - The currency to format the amount in.
 * @returns The formatted currency string.
 */
export const numberWithCurrency = (
  amount: number,
  currency: string,
): string => {
  const locale = navigator.language || 'en-EN'
  return amount.toLocaleString(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
  })
}

const openChurnSurvey = (isOnTrial: boolean): void => {
  openGlobalModal(MODALS.churnSurvey, {
    isOnTrial,
  })
}

const openConfirmCancellationModal = (
  subscriptionType: 'plan' | 'trial',
): void => {
  openGlobalModal(MODALS.confirmCancellation, {
    subscriptionType,
  })
}

const openPlanSelector = (
  cta: string,
  upgradePathName = 'billing-upgrade',
): void => {
  openGlobalModal(MODALS.planSelector, {
    cta,
    upgradePathName,
  })
}

const RemoveSubscriptionScheduleAlertDialog = ({
  currentPlanName,
  currentChannelCount,
  children,
}: {
  currentPlanName: string
  currentChannelCount: number
  children: React.ReactNode
}): JSX.Element => {
  const organizationId = useOrganizationId()
  const [removeSubscriptionSchedule, { loading: isRemoveScheduleLoading }] =
    useMutation(REMOVE_SUBSCRIPTION_SCHEDULE, {
      onCompleted: (data) => {
        if (
          data?.removeSubscriptionSchedule?.__typename ===
            'RemoveSubscriptionScheduleResponse' &&
          data?.removeSubscriptionSchedule?.success
        ) {
          toast.success('Plan reverted successfully.')
        }
        if (
          data?.removeSubscriptionSchedule?.__typename ===
          'RemoveSubscriptionScheduleError'
        ) {
          toast.error(
            `Error removing scheduled update. Please try again or contact support.`,
          )
        }
      },
      onError: () => {
        toast.error(
          `Error removing scheduled update. Please try again or contact support.`,
        )
      },
      refetchQueries: [
        'Account',
        'BillingPage',
        {
          query: GetPlanSelectorData,
          variables: {
            input: {
              organizationId: organizationId ?? '',
            },
          },
        },
      ],
    })

  return (
    <AlertDialog>
      <AlertDialog.Trigger>{children}</AlertDialog.Trigger>
      <AlertDialog.Content>
        <AlertDialog.Title>
          Revert to your previous Buffer plan
        </AlertDialog.Title>
        <AlertDialog.Description>
          Are you sure you want to revert to your previous subscription of the{' '}
          <b>{currentPlanName}</b> plan with <b>{currentChannelCount}</b>{' '}
          channels?
        </AlertDialog.Description>
        <AlertDialog.Actions>
          <AlertDialog.Cancel>
            <Button variant="secondary">Cancel</Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button
              variant="critical"
              onClick={(): void => {
                void removeSubscriptionSchedule({
                  variables: {
                    organizationId: organizationId ?? '',
                  },
                })
              }}
              loading={isRemoveScheduleLoading}
            >
              Confirm
            </Button>
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}

type KeepMyPlanCommonTrackingProperties = NonNullable<
  ResultOf<typeof OrganizationDataForBilling_Organization>
>['commonTrackingProperties']

const KeepMyPlanAlertDialog = ({
  children,
  organizationId,
  commonTrackingProperties,
  defaultOpen = false,
}: {
  children: React.ReactNode
  organizationId: string
  commonTrackingProperties: KeepMyPlanCommonTrackingProperties
  defaultOpen?: boolean
}): JSX.Element => {
  const [open, setOpen] = React.useState(defaultOpen)
  const [keepSubscriptionActive, { loading }] = useMutation(
    KEEP_SUBSCRIPTION_ACTIVE,
    {
      onCompleted: (data) => {
        // The mutation payload is a union of KeepSubscriptionActiveSuccess and
        // VoidMutationError — onCompleted fires for both, so gate on __typename
        // to avoid showing the success toast when the server returned a typed
        // business-logic error.
        const payload = data?.keepSubscriptionActive
        if (payload?.__typename === 'KeepSubscriptionActiveSuccess') {
          toast.success('Your plan has been kept active.')
        } else {
          toast.error(
            payload?.__typename === 'VoidMutationError' && payload.message
              ? payload.message
              : 'Something went wrong. Please try again or contact support.',
          )
        }
        setOpen(false)
      },
      onError: () => {
        toast.error(
          'Something went wrong. Please try again or contact support.',
        )
        setOpen(false)
      },
      refetchQueries: [
        'Account',
        'BillingPage',
        {
          query: GetPlanSelectorData,
          variables: {
            input: {
              organizationId,
            },
          },
        },
      ],
    },
  )

  // Stash the latest tracking props in a ref so the modal-viewed effect can
  // depend solely on `open`. Apollo doesn't guarantee a stable identity for
  // `commonTrackingProperties` across cache writes; including it in the deps
  // can re-fire the event while the modal is still open.
  const trackingRef = React.useRef({ organizationId, commonTrackingProperties })
  trackingRef.current = { organizationId, commonTrackingProperties }

  React.useEffect(() => {
    if (!open) return
    BufferTracker.keepPlanModalViewed({
      organizationId: trackingRef.current.organizationId,
      ...trackingRef.current.commonTrackingProperties,
    })
  }, [open])

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next): void => {
        // Don't let the user dismiss while the mutation is in flight; the
        // onCompleted/onError handlers close the modal once Stripe responds.
        if (loading) return
        setOpen(next)
      }}
    >
      <AlertDialog.Trigger>{children}</AlertDialog.Trigger>
      <AlertDialog.Content>
        <AlertDialog.Title>Keep My Plan Active</AlertDialog.Title>
        <AlertDialog.Description>
          Your pending cancellation will be removed and your subscription will
          continue as normal.
        </AlertDialog.Description>
        <AlertDialog.Actions>
          <AlertDialog.Cancel>
            <Button
              variant="secondary"
              disabled={loading}
              onClick={(): void => {
                // Defensive guard: AlertDialog.Cancel renders via Radix
                // `asChild`, so we don't fully control whether a click during
                // loading still fires this handler. Bail out so we don't emit
                // a stray tracking event while the mutation is in flight.
                if (loading) return
                BufferTracker.keepPlanCTAClicked({
                  organizationId,
                  ctaName: 'never-mind',
                  ...commonTrackingProperties,
                })
              }}
            >
              Never Mind
            </Button>
          </AlertDialog.Cancel>
          <Button
            variant="primary"
            onClick={(): void => {
              BufferTracker.keepPlanCTAClicked({
                organizationId,
                ctaName: 'keep',
                ...commonTrackingProperties,
              })
              void keepSubscriptionActive({
                variables: {
                  input: { organizationId },
                },
              })
            }}
            loading={loading}
            disabled={loading}
          >
            Keep My Plan
          </Button>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}

/**
 * A smart component that renders either a list or a single text element based on the number of items.
 */
const PlanDetailsList = ({
  children,
}: {
  children: React.ReactNode[]
}): JSX.Element => {
  // Filter out falsy values (null, undefined, false) to get actual item count
  const items = React.Children.toArray(children).filter(
    (item): item is React.ReactElement => React.isValidElement(item),
  )

  if (items.length === 0) {
    return <></>
  }

  if (items.length === 1) {
    // For a single item, extract the content from inside the li
    return <>{items[0].props.children}</>
  }

  return <ul className={styles.planCardList}>{items}</ul>
}

/**
 * Renders the plan card body for the current WebBillingDetails subscription —
 * plan badge, trial/free/cancelled/credit-balance copy, Change Plan / Cancel actions,
 * scheduled-update notice, and the PlanCardsVertical child.
 */
export const PlanContent = ({
  webBilling: webBillingRef,
  organizationData,
}: {
  webBilling: FragmentOf<typeof PlanContent_WebBillingDetails> &
    FragmentOf<typeof PlanCardsVertical_WebBillingDetails>
  organizationData: FragmentOf<
    typeof OrganizationDataForBilling_Organization
  > | null
}): JSX.Element => {
  const webBilling = readFragment(PlanContent_WebBillingDetails, webBillingRef)
  const organization = organizationData
    ? readFragment(OrganizationDataForBilling_Organization, organizationData)
    : null
  const subscription = webBilling.subscription

  // Deflection-email deeplink: /settings/billing/keep-my-plan auto-opens the
  // Keep My Plan dialog so users can reverse a pending cancellation in one click.
  // We capture the value once on mount so the deeplink fires exactly once per
  // page load. Both the auto-open and the URL cleanup are gated on
  // isCanceledAtPeriodEnd — if the subscription isn't actually canceled, the
  // deeplink is a no-op (no modal, no URL rewrite).
  const location = useLocation()
  const navigate = useNavigate()
  const [isKeepMyPlanDeeplink] = React.useState(
    () => location.pathname === settingsBillingKeepMyPlanRoute.route,
  )
  const shouldConsumeDeeplink =
    !webBilling.requiresAssistedBilling &&
    isKeepMyPlanDeeplink &&
    subscription.isCanceledAtPeriodEnd
  React.useEffect(() => {
    if (!shouldConsumeDeeplink) return
    navigate(settingsBillingRoute.route, { replace: true })
  }, [shouldConsumeDeeplink, navigate])

  const scheduledUpdate = subscription.scheduledUpdate

  // Only show scheduled update if it's today or in the future
  const isFutureScheduledUpdate =
    scheduledUpdate &&
    new Date(scheduledUpdate.effectiveDate) >=
      new Date(new Date().setHours(0, 0, 0, 0))

  const displayScheduledUpdate = isFutureScheduledUpdate
    ? scheduledUpdate
    : null

  // WebBillingDetails.balance.amount is in cents; getCreditBalanceCopy expects dollars.
  const creditBalance = webBilling.balance.amount / 100
  const creditBalanceCopy = getCreditBalanceCopy(creditBalance)

  const channelsLimit = organization?.limits.channels ?? null

  // WebBillingDetails dropped the `trial.isActive` scalar; derive from start/end dates
  // (mirrors the pattern from PaymentDetails in VLG-694).
  const now = new Date()
  const isOnTrial =
    subscription.trial !== null &&
    new Date(subscription.trial.startDate) <= now &&
    now < new Date(subscription.trial.endDate)
  const trialRemainingDays =
    subscription.trial && isOnTrial
      ? Math.ceil(
          (new Date(subscription.trial.endDate).getTime() - now.getTime()) /
            MS_PER_DAY,
        )
      : 0
  const isOnTrialEndDate = subscription.trial?.endDate
    ? new Date(subscription.trial.endDate).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  // The `paymentMethod` enum string is replaced by a discriminated union on
  // PaymentMethodDetails; bank payments are identified by __typename.
  const isPayingWithBank =
    webBilling.paymentMethodDetails?.__typename === 'BankPaymentMethodDetails'

  const isFreePlan = subscription.plan.id === OBPlanIdValues.free

  const renewalDate = subscription.periodEnd
    ? new Date(subscription.periodEnd).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  const canceledDate = subscription.canceledAt
    ? new Date(subscription.canceledAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  const handleChangePlan = (): void => {
    if (isOnTrial) {
      openPlanSelector('billing-plansCard-changePlanFromTrial-1')
    } else if (isFreePlan) {
      openPlanSelector('billing-plansCard-upgrade-1')
    } else {
      openPlanSelector('billing-plansCard-changePlanFromPaid-1')
    }
  }

  const handleCancelPlanClick = (): void => {
    if (!organization) {
      return
    }
    if (isOnTrial) {
      openConfirmCancellationModal('trial')
    } else {
      openChurnSurvey(isOnTrial)
    }
    BufferTracker.cancelPlanClicked({
      organizationId: organization.id,
      ...organization.commonTrackingProperties,
    })
  }

  return (
    <>
      <Flex direction="column" gap="sm" fullWidth>
        {webBilling.requiresAssistedBilling ? (
          <AssistedBillingNotice />
        ) : (
          isPayingWithBank && (
            <Notice variant="neutral">
              <Text>
                To make changes to your plan, please contact support at{' '}
                <Link href="mailto:support@buffer.com">support@buffer.com</Link>
                .
              </Text>
            </Notice>
          )
        )}
        {/* Header */}
        <Flex
          fullWidth
          className={clsx(
            styles.container,
            subscription.isCanceledAtPeriodEnd && styles.containerCanceled,
          )}
        >
          {/* Plan details */}
          <Flex direction="column" gap="xs">
            {/* Plan name and trial status */}
            <Flex direction="row" gap="xs" align="center">
              <Heading as="h2" size="small">
                Plan:
              </Heading>
              <Badge
                size="medium"
                variant="brand-subtle"
                style={{ textTransform: 'uppercase' }}
              >
                {subscription.plan.name}
              </Badge>
              {isOnTrial && (
                <Badge
                  size="medium"
                  variant="neutral"
                  style={{ textTransform: 'uppercase' }}
                >
                  Trial
                </Badge>
              )}
            </Flex>
            {/* Renewal date and other details */}
            <PlanDetailsList>
              {/* Paid plans canceled at period end */}
              {subscription.isCanceledAtPeriodEnd && (
                <li>
                  <Text size="md">
                    {canceledDate &&
                      `Your plan was changed on ${canceledDate}. `}
                    You can continue using the{' '}
                    <strong>{capitalize(subscription.plan.name)}</strong>{' '}
                    features until the end of your billing cycle on{' '}
                    <strong>{renewalDate}</strong>, when your Free subscription
                    starts.
                  </Text>
                </li>
              )}
              {/* Free plan */}
              {isFreePlan && (
                <li>
                  <Text size="md" className={styles.freePlanText}>
                    The Free plan is limited to 3 channels. Upgrade to the
                    Essentials plan and connect as many channels as you&apos;d
                    like.
                  </Text>
                </li>
              )}
              {/* Trial */}
              {isOnTrial && (
                <li>
                  <Text size="md">
                    Your free trial ends in{' '}
                    <strong>{trialRemainingDays} days</strong> on{' '}
                    <strong>{isOnTrialEndDate}</strong>.
                  </Text>
                </li>
              )}
              {/* Credit balance */}
              {creditBalanceCopy && (
                <li>
                  <Text size="md">{creditBalanceCopy}</Text>
                </li>
              )}
            </PlanDetailsList>
          </Flex>
          {/* Actions */}
          {!isPayingWithBank && !webBilling.requiresAssistedBilling && (
            <Flex direction="row" gap="2xs" align="center">
              {subscription.isCanceledAtPeriodEnd && organization && (
                <KeepMyPlanAlertDialog
                  organizationId={organization.id}
                  commonTrackingProperties={
                    organization.commonTrackingProperties
                  }
                  defaultOpen={shouldConsumeDeeplink}
                >
                  <Button size="medium" variant="primary">
                    Keep My Plan
                  </Button>
                </KeepMyPlanAlertDialog>
              )}
              <Button
                size="medium"
                variant="secondary"
                onClick={handleChangePlan}
              >
                Change Plan
              </Button>
              {!isFreePlan && !subscription.isCanceledAtPeriodEnd && (
                <DropdownMenu>
                  <Tooltip content="More plan actions">
                    <DropdownMenu.Trigger asChild>
                      <IconButton
                        label="More plan actions"
                        size="medium"
                        variant="secondary"
                      >
                        <EllipsisVerticalIcon />
                      </IconButton>
                    </DropdownMenu.Trigger>
                  </Tooltip>
                  <DropdownMenu.Content align="end">
                    {displayScheduledUpdate && (
                      <RemoveSubscriptionScheduleAlertDialog
                        currentPlanName={capitalize(subscription.plan.name)}
                        currentChannelCount={channelsLimit ?? 0}
                      >
                        <DropdownMenu.Item
                          onSelect={(e): void => e.preventDefault()}
                        >
                          Revert To Previous Plan
                        </DropdownMenu.Item>
                      </RemoveSubscriptionScheduleAlertDialog>
                    )}
                    {!subscription.isCanceledAtPeriodEnd && (
                      <DropdownMenu.Item
                        variant="critical"
                        onClick={handleCancelPlanClick}
                      >
                        Cancel {isOnTrial ? 'Trial' : 'Plan'}
                      </DropdownMenu.Item>
                    )}
                  </DropdownMenu.Content>
                </DropdownMenu>
              )}
            </Flex>
          )}
        </Flex>

        {displayScheduledUpdate && (
          <Notice variant="warning">
            <Notice.Text>
              This plan is scheduled to change to{' '}
              {capitalize(displayScheduledUpdate.newPlan)} (Billed{' '}
              {displayScheduledUpdate.newInterval}ly, with access to{' '}
              {displayScheduledUpdate.newQuantity}{' '}
              {displayScheduledUpdate.newQuantity === 1
                ? 'channel'
                : 'channels'}
              ) on{' '}
              {new Date(
                displayScheduledUpdate.effectiveDate,
              ).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
              .
            </Notice.Text>
          </Notice>
        )}

        <PlanCardsVertical
          webBilling={webBillingRef}
          organizationData={organizationData}
        />

        <Text size="md">
          Need help?{' '}
          <Button
            type="button"
            variant="tertiary"
            size="small"
            className={inlineTextControls.inlineButton}
            onClick={openBeaconChat}
          >
            Talk to Our Support Team
          </Button>
        </Text>
      </Flex>
    </>
  )
}

export default PlanContent
